export type CartProductTagMatchBy = 'name' | 'handle';

export interface CartProductTagSource {
  id?: string;
  labels?: Array<{ name?: string; handle?: string } | null> | null;
}

export interface CartProductTagShopSource {
  products?: CartProductTagSource[] | null;
  addition_groups?: Array<{ items?: CartProductTagSource[] | null } | null> | null;
}

/**
 * `true` when a cart product carries the tag, `false` when every product was
 * found in the catalog and none matches (or the cart is empty), `null` when
 * a cart product is not in the catalog yet — absence cannot be asserted.
 */
export function cartHasProductTag(
  cartLines: readonly unknown[] | null | undefined,
  catalogProducts: readonly CartProductTagSource[] | null | undefined,
  matchBy: unknown,
  rawValue: unknown,
): boolean | null {
  const needle = typeof rawValue === 'string' ? rawValue.trim() : '';
  if (!needle) {
    return false;
  }

  const ids = collectCartProductIds(cartLines);
  if (ids.length === 0) {
    return false;
  }

  const catalogById = indexCatalog(catalogProducts);
  const normalizedMatchBy: CartProductTagMatchBy = matchBy === 'handle' ? 'handle' : 'name';
  let sawUnresolved = false;

  for (const id of ids) {
    const products = catalogById.get(id);
    if (!products) {
      sawUnresolved = true;
      continue;
    }
    if (products.some(product => productHasTag(product, normalizedMatchBy, needle))) {
      return true;
    }
  }

  return sawUnresolved ? null : false;
}

/** Catalog products plus shop products and addition-group items, which also carry tags. */
export function mergeCartProductTagCatalog(
  products: readonly CartProductTagSource[] | null | undefined,
  shops: readonly CartProductTagShopSource[] | null | undefined,
): CartProductTagSource[] {
  const merged: CartProductTagSource[] = [];
  if (products) {
    merged.push(...products);
  }
  for (const shop of shops ?? []) {
    if (shop?.products) {
      merged.push(...shop.products);
    }
    for (const group of shop?.addition_groups ?? []) {
      if (group?.items) {
        merged.push(...group.items);
      }
    }
  }
  return merged;
}

function collectCartProductIds(cartLines: readonly unknown[] | null | undefined): string[] {
  const ids = new Set<string>();
  if (!cartLines) {
    return [];
  }

  for (const line of cartLines) {
    if (!isRecord(line)) {
      continue;
    }
    pushId(ids, line['item_id']);
    collectAdditionIds(ids, line['additions']);

    const nested = line['products'];
    if (!Array.isArray(nested)) {
      continue;
    }
    for (const child of nested) {
      if (!isRecord(child)) {
        continue;
      }
      pushId(ids, child['id']);
      collectAdditionIds(ids, child['additions']);
      const groups = child['separate_additions_by_group'];
      if (!Array.isArray(groups)) {
        continue;
      }
      for (const group of groups) {
        if (!isRecord(group)) {
          continue;
        }
        collectAdditionIds(ids, group['selected']);
      }
    }
  }

  return [...ids];
}

function collectAdditionIds(ids: Set<string>, additions: unknown): void {
  if (!Array.isArray(additions)) {
    return;
  }
  for (const addition of additions) {
    pushId(ids, addition);
  }
}

function pushId(ids: Set<string>, value: unknown): void {
  if (typeof value === 'string' && value.trim()) {
    ids.add(value);
  }
}

function indexCatalog(
  products: readonly CartProductTagSource[] | null | undefined,
): Map<string, CartProductTagSource[]> {
  const byId = new Map<string, CartProductTagSource[]>();
  for (const product of products ?? []) {
    const id = product?.id;
    if (typeof id !== 'string' || !id) {
      continue;
    }
    const list = byId.get(id);
    if (list) {
      list.push(product);
    } else {
      byId.set(id, [product]);
    }
  }
  return byId;
}

function productHasTag(
  product: CartProductTagSource,
  matchBy: CartProductTagMatchBy,
  needle: string,
): boolean {
  if (!Array.isArray(product.labels)) {
    return false;
  }
  return product.labels.some(label => {
    if (!label) {
      return false;
    }
    const field = matchBy === 'handle' ? label.handle : label.name;
    if (typeof field !== 'string') {
      return false;
    }
    const candidate = field.trim();
    if (matchBy === 'handle') {
      return candidate === needle;
    }
    return candidate.localeCompare(needle, undefined, { sensitivity: 'accent' }) === 0;
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object';
}
